/* eslint-disable no-nested-ternary -- let it be! */
import type { FC, ReactNode } from "react";
import { cx } from "../../../utils";

interface FieldSetProps {
  title: string;
  children: ReactNode;
  className?: string;
  legendClassName?: string;
  variant?: "underline" | "card" | "chevron";
}
/**
 * Used for grouping form fields.
 *
 * For instance when you have a form which can add multiple instances of
 * itself, you can use this component to group the fields together.
 *
 * Requires to be wrapper inside of a <form> component
 *
 * Props:
 * @param title - the title of the fieldset
 * @param children - the children of the fieldset
 * @param className - the className of the fieldset
 * @param legendClassName - the className of the legend
 */
export const FieldSet: FC<FieldSetProps> = ({
  title,
  children,
  legendClassName,
  className,
  variant = "underline"
}) => {
  const cardStyles =
    variant === "underline"
      ? "border-b pb-3 border-alpha-40"
      : "border px-4 pb-6 pt-0 bg-white rounded-lg border border-alpha-100 shadow-xs relative";

  const legendStyles =
    variant === "underline"
      ? "w-full uppercase text-primary-500 text-xs font-bold pb-2 mb-4 border-b border-primary-500"
      : variant === "chevron"
        ? "shadow-inner right-2 top-0 -translate-y-full rounded-t-lg font-bold bg-gradient-to-b from-primary-300 to-primary-500 text-white absolute bg text-sm bg px-3 py-0.5"
        : "text-xs sticky top-0 pt-6 mb-3 block bg-white  w-full z-[1] text-content-200 pb-2 border-b border-alpha-100 font-bold";

  return (
    <fieldset className={cx(cardStyles, className)}>
      {!title ? null : variant === "underline" ? ( // Legend has positioning behavior
        <legend className={cx(legendStyles, legendClassName)}>{title}</legend>
      ) : (
        <p className={cx(legendStyles, legendClassName)}>{title}</p>
      )}
      {children}
    </fieldset>
  );
};
